import { ClockIcon } from '@langchain/untitled-ui-icons';

import { ReactNode } from 'react';

import { calculateLatency } from '../Pages/Run/components/RunStatsCard';
import { RunSchema } from '../types/schema';
import { getLatencyThemePercentile } from '../utils/latencyTheme';
import { cn } from '../utils/tailwind';

function getLatencyThemeClass(
  latency: string | number,
  percentiles?: { p50?: number; p99?: number }
): string | undefined {
  // when stats percentiles are loading show gray background.
  if (percentiles && (!percentiles.p50 || !percentiles.p99)) {
    return 'bg-tertiary';
  }

  return getLatencyThemePercentile(latency, percentiles?.p50, percentiles?.p99);
}

export function LatencyChip(props: {
  latency: string | number | undefined;
  size?: 'xs' | 'sm';
  label?: ReactNode | undefined;
  className?: string;
  testId?: string;
  simple?: boolean;
  maximumFractionDigits?: number;
  percentiles?: {
    p50?: number;
    p99?: number;
  };
}) {
  if (props.latency == null) return null;
  const size = props.size ?? 'xs';

  const latencyLabel =
    typeof props.latency === 'number'
      ? `${props.latency.toFixed(props.maximumFractionDigits ?? 2)}s`
      : props.latency;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md bg-background',
        {
          'py-0.5 text-xs': size === 'xs',
          'py-0 text-sm': size === 'sm',
        },
        getLatencyThemeClass(props.latency, props.percentiles),
        props.label ? 'px-1.5' : 'pl-1 pr-1.5',
        props.simple && 'border-none',
        props.className
      )}
      data-testid={props.testId}
    >
      {props.label ? (
        props.label
      ) : props.simple ? null : (
        <ClockIcon className="size-4" />
      )}
      {latencyLabel}
    </div>
  );
}

export function RunLatencyChip({
  run,
  size = 'xs',
  className,
  percentiles,
}: {
  run: Pick<RunSchema, 'start_time' | 'end_time'>;
  size?: 'xs' | 'sm';
  className?: string;
  percentiles?: {
    p50?: number;
    p99?: number;
  };
}) {
  const { start_time, end_time } = run;
  const { latency } = end_time
    ? calculateLatency(start_time, end_time)
    : { latency: undefined };

  return (
    <LatencyChip
      latency={latency}
      className={className}
      size={size}
      percentiles={percentiles}
    />
  );
}
